import React, { useEffect, useState } from 'react';

import { ButtonRounded, Checkbox, Notification, TextInput, Transition } from 'is24-corecss';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NameMismatchDetails, RejectionReason } from '../../api/tvs/customer/model/CustomerVerificationStatus';
import { getCustomerVerificationStatus } from '../../api/tvs/customer/customerVerificationStatus';
import { ApiResponseError } from '../../api/ApiResponseError';
import {
  completeUploadSession,
  createUploadSession,
  uploadFileToPresignedUrl,
} from '../../api/tvs/uploadSessions/uploadSessionApi';
import CompanyFormSelect, {
  CompanyFormOptionKey,
  defaultCompanyFormOption,
} from '../../components/companyFormSelect/CompanyFormSelect';
import FileUpload, { AttachedFile } from '../../components/fileUpload/FileUpload';
import { DSA_DOCUMENTS_PENDING_PATH } from '../../routePaths';
import { CompleteUploadSessionFrontendStatus } from '../../api/tvs/uploadSessions/model/UploadSession';
import {
  ACCEPTED_DOCUMENT_FILE_TYPES,
  getDocumentContentType,
  MAX_DOCUMENT_FILE_SIZE_BYTES,
  MAX_DOCUMENT_FILE_SIZE_MB,
  requiresBusinessLicense,
} from './documentUploadConfig';
import { checkIban, isIbanEmpty } from './validators/Validators';
import { routeWithSearch } from '../../utils/routeWithSearch';

interface RequestedUploadFile {
  documentType: 'ID_CARD' | 'BUSINESS_LICENSE';
  file: File;
  contentType: NonNullable<ReturnType<typeof getDocumentContentType>>;
  fileName: string;
}

interface KybLandingPageProps {
  documentCheckFailed?: boolean;
  errorReason?: RejectionReason | null;
  nameMismatch?: NameMismatchDetails | null;
}

const CUSTOMER_FACING_REJECTION_REASONS = new Set([
  'invalid_upload_request',
  'upload_already_in_progress',
  'missing_required_documents',
  'upload_session_not_found',
  'company_name_mismatch',
  'company_not_found',
  'business_registration_required',
  'company_name_missing',
  'company_inactive_or_dissolved',
  'document_expired',
  'representative_mismatch',
  'salesforce_contact_mismatch',
  'missing_hrb_document',
  'file_not_uploaded',
  'document_not_accepted',
  'unknown',
]);

const customerFacingRejectionReason = (reason?: string | null): string | null => {
  if (!reason) return null;
  return CUSTOMER_FACING_REJECTION_REASONS.has(reason) ? reason : 'document_not_accepted';
};

const hasInvalidFiles = (files: AttachedFile[]): boolean => {
  return files.some(({ validationErrorMessage }) => !!validationErrorMessage);
};

const completeUploadSessionSuccessStatuses: CompleteUploadSessionFrontendStatus[] = [
  'READY_FOR_DISPATCH',
  'READY_FOR_PROCESSING',
  'PROCESSED',
];

type CompletedUploadSessionAction = 'stayOnUploadPage' | 'pollCompleteEndpoint' | 'showUploadError' | 'showSuccessPage';

const COMPLETE_UPLOAD_SESSION_POLL_INTERVAL_MS = 5000;

const wait = (delayMs: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, delayMs));

const getActionForCompletedUploadStatus = (
  status: CompleteUploadSessionFrontendStatus,
): CompletedUploadSessionAction => {
  if (completeUploadSessionSuccessStatuses.includes(status)) {
    return 'showSuccessPage';
  }

  switch (status) {
    case 'CREATED':
      return 'stayOnUploadPage';
    case 'PENDING':
      return 'pollCompleteEndpoint';
    case 'INVALID':
      return 'showUploadError';
    default:
      return 'showUploadError';
  }
};

const completeUploadSessionUntilReady = async (sessionId: string): Promise<CompleteUploadSessionFrontendStatus> => {
  let completedUploadSession = await completeUploadSession(sessionId);

  while (getActionForCompletedUploadStatus(completedUploadSession.status) === 'pollCompleteEndpoint') {
    await wait(COMPLETE_UPLOAD_SESSION_POLL_INTERVAL_MS);
    completedUploadSession = await completeUploadSession(sessionId);
  }

  return completedUploadSession.status;
};

const KybLandingPage: React.FC<KybLandingPageProps> = ({ documentCheckFailed = false, errorReason, nameMismatch }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerFacingErrorReason = customerFacingRejectionReason(errorReason);
  const showMismatchNames =
    (customerFacingErrorReason === 'company_name_mismatch' ||
      customerFacingErrorReason === 'salesforce_contact_mismatch') &&
    !!nameMismatch?.documentName?.trim() &&
    !!nameMismatch?.accountName?.trim();
  const rejectionMessageKey = showMismatchNames ? `${customerFacingErrorReason}_with_names` : customerFacingErrorReason;
  const documentCheckFailedMessage = rejectionMessageKey
    ? (t(`errorMessages.kybRejectionReasons.${rejectionMessageKey}`, {
        documentName: nameMismatch?.documentName.trim(),
        accountName: nameMismatch?.accountName.trim(),
        maxSize: `${MAX_DOCUMENT_FILE_SIZE_MB} MB`,
        defaultValue: t('errorMessages.documentCheckFailed'),
      }) as string)
    : t('errorMessages.documentCheckFailed');
  const triggeredBy = searchParams.get('triggeredBy')?.trim();
  const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const [companyForm, setCompanyForm] = useState<CompanyFormOptionKey>(defaultCompanyFormOption);
  const [showCompanyFormError, setShowCompanyFormError] = useState(false);
  const [identityDocumentFiles, setIdentityDocumentFiles] = useState<AttachedFile[]>([]);
  const [registerExtractFiles, setRegisterExtractFiles] = useState<AttachedFile[]>([]);
  const [iban, setIban] = useState('');
  const [invalidIbanMessage, setInvalidIbanMessage] = useState('');
  const [showIdentityDocumentError, setShowIdentityDocumentError] = useState(false);
  const [showRegisterExtractError, setShowRegisterExtractError] = useState(false);
  const [isSelfCertified, setIsSelfCertified] = useState(false);
  const [showSelfCertificationError, setShowSelfCertificationError] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState('');
  const [showDocumentCheckFailedNotification, setShowDocumentCheckFailedNotification] = useState(documentCheckFailed);
  const [isUploading, setIsUploading] = useState(false);
  const shouldShowRegisterExtractUpload = requiresBusinessLicense(companyForm);

  useEffect(() => {
    if (!isUploading) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading]);

  useEffect(() => {
    if (!triggeredBy) {
      setServerErrorMessage(t('errorMessages.missingTriggeredBy'));
    }
  }, [triggeredBy, t]);

  useEffect(() => {
    setShowDocumentCheckFailedNotification(documentCheckFailed);
  }, [documentCheckFailed]);

  const buildRequestedUploadFiles = (): RequestedUploadFile[] | undefined => {
    const identityUploads = identityDocumentFiles.map(({ file }) => ({
      documentType: 'ID_CARD' as const,
      file,
      contentType: getDocumentContentType(file),
      fileName: file.name,
    }));
    const registerExtractUploads = shouldShowRegisterExtractUpload
      ? registerExtractFiles.map(({ file }) => ({
          documentType: 'BUSINESS_LICENSE' as const,
          file,
          contentType: getDocumentContentType(file),
          fileName: file.name,
        }))
      : [];
    const requestedUploads = [...identityUploads, ...registerExtractUploads];

    if (requestedUploads.some(({ contentType }) => !contentType)) {
      return undefined;
    }

    return requestedUploads as RequestedUploadFile[];
  };

  const validateData = (): boolean => {
    const hasIban = !isIbanEmpty(iban);
    const isIbanValid = checkIban(iban);
    const hasCompanyForm = companyForm !== defaultCompanyFormOption;
    const hasIdentityDocument = identityDocumentFiles.length > 0;
    const hasRegisterExtract = !shouldShowRegisterExtractUpload || registerExtractFiles.length > 0;
    const hasSelfCertification = isSelfCertified;
    const hasNoFileValidationErrors = !hasInvalidFiles(identityDocumentFiles) && !hasInvalidFiles(registerExtractFiles);

    setInvalidIbanMessage(isIbanValid ? '' : t(hasIban ? 'errorMessages.ibanInvalid' : 'errorMessages.ibanInput'));
    setShowCompanyFormError(!hasCompanyForm);
    setShowIdentityDocumentError(!hasIdentityDocument);
    setShowRegisterExtractError(!hasRegisterExtract);
    setShowSelfCertificationError(!hasSelfCertification);
    setServerErrorMessage('');

    return (
      isIbanValid &&
      hasCompanyForm &&
      hasIdentityDocument &&
      hasRegisterExtract &&
      hasSelfCertification &&
      hasNoFileValidationErrors
    );
  };

  const uploadDocument = async () => {
    if (isUploading) return;
    if (!validateData()) return;

    if (!triggeredBy) {
      setServerErrorMessage(t('errorMessages.missingTriggeredBy'));
      return;
    }

    const requestedUploads = buildRequestedUploadFiles();

    if (!requestedUploads) {
      return;
    }

    try {
      setIsUploading(true);
      const uploadSession = await createUploadSession({
        businessType: companyForm,
        triggeredBy,
        uploads: requestedUploads.map(({ documentType, contentType, fileName }) => ({
          documentType,
          contentType,
          fileName,
        })),
      });

      const requestedUploadsByFileName = new Map(requestedUploads.map((upload) => [upload.fileName, upload.file]));

      await Promise.all(
        uploadSession.uploads.map((uploadTarget) => {
          const file = requestedUploadsByFileName.get(uploadTarget.fileName);

          if (!file) {
            throw new Error(`Missing selected file for upload target: ${uploadTarget.fileName}`);
          }

          return uploadFileToPresignedUrl(uploadTarget, file);
        }),
      );
      const completedUploadStatus = await completeUploadSessionUntilReady(uploadSession.sessionId);
      const completedUploadAction = getActionForCompletedUploadStatus(completedUploadStatus);

      if (completedUploadAction === 'showSuccessPage') {
        navigate(routeWithSearch(DSA_DOCUMENTS_PENDING_PATH, search));
      }

      if (completedUploadAction === 'showUploadError') {
        try {
          const status = await getCustomerVerificationStatus();
          const customerFacingReason = customerFacingRejectionReason(status.rejectionReason);
          setServerErrorMessage(
            customerFacingReason
              ? t(`errorMessages.kybRejectionReasons.${customerFacingReason}`, {
                  maxSize: `${MAX_DOCUMENT_FILE_SIZE_MB} MB`,
                  defaultValue: t('errorMessages.uploadFailed'),
                })
              : t('errorMessages.uploadFailed'),
          );
        } catch {
          setServerErrorMessage(t('errorMessages.uploadFailed'));
        }
      }

      // CREATED means the backend accepted the call but the session is not complete yet.
      // Keep the user on the upload page so they can try again.
    } catch (error) {
      let reason: string | undefined;
      if (error instanceof ApiResponseError && error.response) {
        try {
          const body = (await error.response.json()) as { reason?: string };
          reason = body.reason;
        } catch {
          // Keep the generic upload error when the response has no readable reason.
        }
      }
      const customerFacingReason = customerFacingRejectionReason(reason);
      setServerErrorMessage(
        customerFacingReason
          ? t(`errorMessages.kybRejectionReasons.${customerFacingReason}`, {
              maxSize: `${MAX_DOCUMENT_FILE_SIZE_MB} MB`,
              defaultValue: t('errorMessages.uploadFailed'),
            })
          : t('errorMessages.uploadFailed'),
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className='margin-top-l margin-bottom-xxl'>
      <div className='font-heading-medium-bold margin-bottom-xl'>{t('KybLandingPage.heading')}</div>
      <div className='margin-bottom-xxl'>{t('KybLandingPage.intro')}</div>

      {showDocumentCheckFailedNotification && (
        <Notification
          className='margin-bottom-xl'
          title={t('errorMessages.documentCheckFailedHeadline')}
          message={documentCheckFailedMessage}
          type='error'
          showCloseIcon={true}
          onClose={() => setShowDocumentCheckFailedNotification(false)}
        />
      )}

      <div className='font-heading-xsmall-bold margin-bottom-m'>{t('KybLandingPage.identityDocumentLabel')}</div>
      <div className='margin-bottom-l'>{t('KybLandingPage.identityDocumentDescription')}</div>
      <div className='grid grid-flex'>
        <div className='grid-item one-whole lap-one-half desk-one-half'>
          <FileUpload
            title={t('FileUpload.identityDocumentTitle')}
            accept={ACCEPTED_DOCUMENT_FILE_TYPES}
            maxFileSizeBytes={MAX_DOCUMENT_FILE_SIZE_BYTES}
            maxFileSizeErrorMessage={t('errorMessages.fileTooLarge', { maxSizeMb: MAX_DOCUMENT_FILE_SIZE_MB })}
            files={identityDocumentFiles}
            disabled={isUploading}
            onFilesChange={(files) => {
              setIdentityDocumentFiles(files);
              if (files.length > 0) setShowIdentityDocumentError(false);
              setServerErrorMessage('');
            }}
            hasError={showIdentityDocumentError}
            errorMessage={t('errorMessages.identityDocumentUpload')}
          />
        </div>
      </div>

      <div className='font-heading-xsmall-bold margin-top-xxl margin-bottom-m'>
        {t('KybLandingPage.companyFormLabel')}
      </div>
      <div className='grid grid-flex'>
        <div className='grid-item one-whole lap-one-half desk-one-half'>
          <CompanyFormSelect
            value={companyForm}
            onChange={(value) => {
              setCompanyForm(value);
              setShowCompanyFormError(false);
              setServerErrorMessage('');
            }}
            hasError={showCompanyFormError}
            errorMessage={t('errorMessages.companyForm')}
            disabled={isUploading}
          />
        </div>
      </div>

      {shouldShowRegisterExtractUpload && (
        <div className='grid grid-flex'>
          <div className='grid-item one-whole lap-one-half desk-one-half margin-top-l margin-bottom-l'>
            {t('KybLandingPage.registerExtractDescription')}
          </div>
          <div className='grid-item one-whole margin-bottom-m'>{t('KybLandingPage.registerExtractUploadLabel')}</div>
          <div className='grid-item one-whole lap-one-half desk-one-half'>
            <FileUpload
              title={t('FileUpload.registerExtractTitle')}
              accept={ACCEPTED_DOCUMENT_FILE_TYPES}
              maxFileSizeBytes={MAX_DOCUMENT_FILE_SIZE_BYTES}
              maxFileSizeErrorMessage={t('errorMessages.fileTooLarge', { maxSizeMb: MAX_DOCUMENT_FILE_SIZE_MB })}
              files={registerExtractFiles}
              disabled={isUploading}
              onFilesChange={(files) => {
                setRegisterExtractFiles(files);
                if (files.length > 0) setShowRegisterExtractError(false);
                setServerErrorMessage('');
              }}
              hasError={showRegisterExtractError}
              errorMessage={t('errorMessages.registerExtractUpload')}
            />
          </div>
        </div>
      )}
      <div className='grid grid-flex margin-top-l margin-bottom-m'>
        <div className='grid-item one-whole lap-one-half desk-one-half'>
          <div className='grid grid-flex'>
            <div className='grid-item one-whole font-heading-xsmall-bold margin-bottom-m margin-top-m'>
              {t('IbanInput.heading')}
            </div>
            <div className='grid-item one-whole'>
              <TextInput
                id='iban'
                label={t('IbanInput.label')}
                placeholder=''
                required={true}
                mandatory={true}
                onChange={(event) => {
                  setIban(event.target.value);
                  setInvalidIbanMessage('');
                }}
                disabled={isUploading}
                valid={!invalidIbanMessage}
                captionText={invalidIbanMessage}
              />
            </div>
          </div>
        </div>
      </div>

      <div className='font-heading-xsmall-bold margin-top-xxl margin-bottom-m'>
        {t('KybLandingPage.selfCertificationLabel')}
      </div>
      <Checkbox
        id='checkbox-1'
        label={t('KybLandingPage.selfCertificationText')}
        checked={isSelfCertified}
        isErroneous={showSelfCertificationError}
        helperText={showSelfCertificationError ? t('errorMessages.selfCertification') : undefined}
        disabled={isUploading}
        onChange={(event) => {
          const isChecked = event.target.checked;
          setIsSelfCertified(isChecked);
          if (isChecked) setShowSelfCertificationError(false);
        }}
      />
      <div className='margin-top-xxl'>{t('KybLandingPage.continueHint')}</div>
      <hr className='divider margin-top-xxl margin-bottom-m' />
      <Transition in={!!serverErrorMessage} enter='slide' exit='fade'>
        <Notification
          title={t('errorMessages.uploadFailedHeadline')}
          message={serverErrorMessage}
          type='error'
          onClose={() => setServerErrorMessage('')}
        />
      </Transition>
      <div className='grid grid-flex grid-justify-end palm-one-whole'>
        <div className='grid-item palm-one-whole'>
          <ButtonRounded
            className='palm-one-whole'
            label={t('KybLandingPage.uploadingButton')}
            variant='filledBrand'
            widthMode='expand'
            loading={isUploading}
            disabled={isUploading}
            onClick={uploadDocument}
          />
        </div>
      </div>
    </div>
  );
};

export default KybLandingPage;
